import { useState, useEffect } from 'react';
import { infoService, SymbolPrecisionsResponse } from '../services/infoService';

/**
 * Symbol precision data with added helper functions
 */
export interface SymbolPrecisionData {
  symbols: string[];
  precisions: SymbolPrecisionsResponse;
  isLoading: boolean;
  error: Error | null;
  
  // Helper functions
  formatPrice: (symbol: string, price: number) => string;
  formatSize: (symbol: string, size: number) => string;
  getPriceStep: (symbol: string) => number;
  getSizeStep: (symbol: string) => number;
}

/**
 * Custom hook to fetch and provide the list of available trading symbols
 * along with their precision data.
 * 
 * @returns An object containing the symbols array, precisions data, and helper formatting functions
 */
export const useSymbolPrecisions = (): SymbolPrecisionData => {
  const [symbols, setSymbols] = useState<string[]>([]);
  const [precisions, setPrecisions] = useState<SymbolPrecisionsResponse>({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      console.log('useSymbolPrecisions: Starting to fetch symbols and precisions...');
      try {
        setIsLoading(true);
        setError(null);
        
        // Fetch both data sets in parallel
        console.log('useSymbolPrecisions: Calling API services...');
        const [symbolsData, precisionsData] = await Promise.all([
          infoService.getAvailableSymbols(),
          infoService.getSymbolPrecisions()
        ]);
        
        console.log(`useSymbolPrecisions: Received ${symbolsData.length} symbols and ${Object.keys(precisionsData).length} precisions`);
        setSymbols(symbolsData);
        setPrecisions(precisionsData);
      } catch (err) {
        console.error('Error in useSymbolPrecisions hook:', err);
        setError(err instanceof Error ? err : new Error('Failed to fetch symbol data'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper function to format price according to symbol's precision
  const formatPrice = (symbol: string, price: number): string => {
    if (!price && price !== 0) return 'N/A';
    
    const precision = precisions[symbol]?.price_decimals || 2; // Default to 2 decimals
    return price.toFixed(precision);
  };

  // Helper function to format size according to symbol's precision
  const formatSize = (symbol: string, size: number): string => {
    if (!size && size !== 0) return 'N/A';
    
    const precision = precisions[symbol]?.size_decimals || 8; // Default to 8 decimals
    return size.toFixed(precision);
  };

  // Helper function to get the minimum price step for a symbol
  const getPriceStep = (symbol: string): number => {
    const precision = precisions[symbol]?.price_decimals || 2;
    return parseFloat(`0.${'0'.repeat(precision-1)}1`);
  };

  // Helper function to get the minimum size step for a symbol
  const getSizeStep = (symbol: string): number => {
    const precision = precisions[symbol]?.size_decimals || 8;
    return parseFloat(`0.${'0'.repeat(precision-1)}1`);
  };

  return { 
    symbols, 
    precisions, 
    isLoading, 
    error,
    formatPrice,
    formatSize,
    getPriceStep,
    getSizeStep
  };
}; 